import { useMemo, useState } from "react";

import {
  buildRecommenderInput,
  combineRecommenderVerdict,
  type RecommenderFormState,
} from "./recommender-input";
import { calculateAllEEligibility } from "../../../functions/_lib/alle-check/calculator";
import type { AllEOutput, CheckStatus } from "../../../functions/_lib/alle-check/types";
import { HelpIcon } from "./CheckForm";
import { localeOf, useLang, useT, type Lang } from "../../i18n/language";

// Help copy for sidebar stats + check-rows. 1-2 sentence each.
// mirrors HTML help-block intent (alle-check.html lines 339-610).
const STAT_HELP: Record<string, string> = {
  warmtevraag:
    "De jaarlijkse energievraag voor verwarming in kWh, afgeleid van het gasverbruik (route Gas) of EP1 (route BENG1).",
  capaciteitsdekking:
    "Hoeveel procent van de piekvraag bij -7 graden buiten de geselecteerde Quatt-pomp dekt. >=100% = ruim voldoende.",
  aanbevolen_pomp:
    "Quatt All-Electric Solo of Duo, gekozen op basis van warmtevraag, bestaand afgiftesysteem en aanvoertemperatuur (W35 / W45 / W55).",
  max_binnentemp:
    "De geschatte hoogst haalbare binnentemperatuur op een -7 graden dag bij dekking <100%. Lager dan 20 graden = comfortrisico.",
};

const CHECK_HELP: Record<string, string> = {
  woningtype:
    "Blokkeert appartementen zonder VVE-toestemming of zonder buitenruimte, en heating-types die niet samenwerken met All-Electric (luchtverwarming, stadsverwarming).",
  warmtevraag:
    "Vergelijkt de jaarlijkse warmtevraag met de capaciteit van Solo/Duo bij -7 graden. Bepaalt welk model wordt aanbevolen.",
  watertemperatuur:
    "Controleert of het afgiftesysteem voldoende vermogen levert op de lagere aanvoertemperatuur van de warmtepomp (W35 / W45 / W55).",
  thermostaatgedrag:
    "Zone-thermostaten + grote dag/nacht-setbacks zijn ongunstig voor een warmtepomp die het beste werkt met &eacute;&eacute;n constante regeling.",
  isolatie:
    "Bij energielabel D-G is All-Electric meestal niet geschikt zonder eerst extra isolatie. Bij B/C is het een aandachtspunt.",
  radiatoren:
    "Controleert of het totale radiatorvermogen bij W55 voldoende is voor het ingevulde deel van de woning. alleen relevant bij hoge CV-aanvoer (>60 graden) en radiator-systeem.",
};

interface Props {
  formState: RecommenderFormState | null;
}

type Pump = NonNullable<AllEOutput["check1_heat_demand"]["recommended_pump"]>;

const PUMP_LABEL: Record<Pump, string> = {
  single: "All-Electric Solo",
  duo: "All-Electric Duo",
  not_possible: "Niet mogelijk",
};

const PUMP_WHY: Record<Pump, string> = {
  single: "Een buitenunit volstaat. De warmtevraag past binnen Solo-capaciteit.",
  duo: "Twee buitenunits nodig om de piekvraag bij -7°C te halen. Solo is te krap.",
  not_possible: "Geen All-Electric configuratie haalt de warmtevraag. Een Quatt Hybrid of eerst aanvullende isolatie is een passender route.",
};

const STATUS_DOT: Record<CheckStatus, "ok" | "warning" | "error"> = {
  geschikt: "ok",
  extra_controle: "warning",
  ongeschikt: "error",
};

const STATUS_LABEL: Record<CheckStatus, string> = {
  geschikt: "OK",
  extra_controle: "CHECK",
  ongeschikt: "BLOK",
};

const VERDICT_PILL: Record<CheckStatus, { className: string; label: string }> = {
  geschikt: { className: "verdict-pill", label: "Aanbevolen" },
  extra_controle: { className: "verdict-pill warning", label: "Extra controle" },
  ongeschikt: { className: "verdict-pill error", label: "Niet geschikt" },
};

function isFormReady(s: RecommenderFormState): boolean {
  if (s.heating_types.length === 0) return false;
  if (s.route === "gas") {
    if (!s.yearly_gas_m3 || !s.summer_avg_gas_m3) return false;
    if (Number(s.yearly_gas_m3) <= 0) return false;
  } else {
    if (!s.beng1_value || !s.floor_area_m2) return false;
  }
  if (s.house_type === "Appartement") {
    if (s.apartment_vve === null || s.apartment_outdoor_space === null) return false;
  }
  return true;
}

function fallbackPump(coverage: number): Pump {
  if (coverage >= 100) return "single";
  if (coverage >= 70) return "duo";
  return "not_possible";
}

function zoneClassFromPump(pump: Pump): "solo" | "duo" | "below" {
  if (pump === "single") return "solo";
  if (pump === "duo") return "duo";
  return "below";
}

function zoneLabelFromPump(pump: Pump): string {
  if (pump === "single") return "Solo volstaat";
  if (pump === "duo") return "Duo nodig";
  return "Buiten bereik";
}

function formatNumber(n: number, lang: Lang): string {
  return new Intl.NumberFormat(localeOf(lang), { maximumFractionDigits: 0 }).format(n);
}

export function LiveSidebar({ formState }: Props) {
  const t = useT();
  const { lang } = useLang();
  const [openStat, setOpenStat] = useState<string | null>(null);
  const [openCheck, setOpenCheck] = useState<string | null>(null);
  const toggleStat = (k: string) => setOpenStat((prev) => (prev === k ? null : k));
  const toggleCheck = (k: string) => setOpenCheck((prev) => (prev === k ? null : k));

  const liveResult = useMemo(() => {
    if (!formState || !isFormReady(formState)) return null;
    try {
      const input = buildRecommenderInput(formState);
      return calculateAllEEligibility(input, lang);
    } catch {
      return null;
    }
  }, [formState, lang]);

  if (!liveResult) {
    return (
      <aside className="sidebar">
        <div className="sidebar-eyebrow">{t("Live aanbeveling")}</div>
        <div className="verdict-block">
          <div className="verdict-title" style={{ color: "var(--muted)", fontSize: 18, fontWeight: 500 }}>
            {t("Vul de check in om de aanbeveling te zien")}
          </div>
        </div>
        <div className="check-strip">
          {(["Woningtype", "Warmtevraag", "Watertemperatuur", "Thermostaatgedrag", "Isolatie"] as const).map((label) => {
            const helpKey = label.toLowerCase();
            const isOpen = openCheck === helpKey;
            return (
              <div className="check-row-block" key={label}>
                <div className="check-row">
                  <div className="check-dot" style={{ background: "rgba(19,26,32,0.12)" }} />
                  <div className="check-label-row" style={{ flex: 1 }}>
                    <div className="check-label" style={{ color: "var(--muted)" }}>{t(label)}</div>
                    <HelpIcon open={isOpen} onClick={() => toggleCheck(helpKey)} label={`${t("Uitleg")} ${t(label)}`} />
                  </div>
                  <div className="check-status">-</div>
                </div>
                <div className={`help-tooltip ${isOpen ? "help-panel-open" : ""}`}>{t(CHECK_HELP[helpKey] ?? "")}</div>
              </div>
            );
          })}
        </div>
      </aside>
    );
  }

  const verdict = combineRecommenderVerdict(liveResult);
  const heat = liveResult.check1_heat_demand;
  const pump: Pump = heat.recommended_pump ?? fallbackPump(heat.coverage_pct);
  const verdictPill = VERDICT_PILL[verdict];
  const showZone = heat.coverage_pct > 0;

  const checkRows: Array<{ label: string; status: CheckStatus; detail?: string }> = [
    { label: "Woningtype", status: liveResult.check0_exclusions.status, detail: liveResult.check0_exclusions.detail },
    { label: "Warmtevraag", status: liveResult.check1_heat_demand.status, detail: liveResult.check1_heat_demand.detail },
    { label: "Watertemperatuur", status: liveResult.check2_water_temp.status, detail: liveResult.check2_water_temp.detail },
    { label: "Thermostaatgedrag", status: liveResult.check3_thermostat.status, detail: liveResult.check3_thermostat.detail },
    { label: "Isolatie", status: liveResult.check4_insulation.status, detail: liveResult.check4_insulation.detail },
  ];

  // Only show radiator capacity row when the engine actually evaluated it.
  const radiatorCapacity = liveResult.check2_water_temp.radiator_capacity;
  if (radiatorCapacity) {
    checkRows.push({ label: "Radiatoren", status: radiatorCapacity.status, detail: radiatorCapacity.detail });
  }

  return (
    <aside className="sidebar">
      <div className="sidebar-eyebrow">{t("Live aanbeveling")}</div>

      <div className="verdict-block">
        <div className="verdict-title">
          {verdict === "ongeschikt" ? t("Niet geschikt") : t(PUMP_LABEL[pump])}
        </div>
        <div className="verdict-why">{verdict === "ongeschikt" ? t(PUMP_WHY.not_possible) : t(PUMP_WHY[pump])}</div>
        <span className={verdictPill.className}>{t(verdictPill.label)}</span>
        {showZone && (
          <div className={`zone-marker ${zoneClassFromPump(pump)}`}>
            <span className="zone-pct">{heat.coverage_pct}%</span>
            <span>{t("dekking bij -7°C")}</span>
            <span style={{ opacity: 0.5 }}>·</span>
            <span>{t(zoneLabelFromPump(pump))}</span>
          </div>
        )}
      </div>

      <div className="check-strip">
        {checkRows.map((r) => {
          const helpKey = r.label.toLowerCase();
          const isOpen = openCheck === helpKey;
          return (
            <div className="check-row-block" key={r.label}>
              <div className="check-row">
                <div className={`check-dot ${STATUS_DOT[r.status]}`} />
                <div className="check-label-row" style={{ flex: 1 }}>
                  <div className="check-label">{t(r.label)}</div>
                  <HelpIcon open={isOpen} onClick={() => toggleCheck(helpKey)} label={`${t("Uitleg")} ${t(r.label)}`} />
                </div>
                <div className="check-status">{t(STATUS_LABEL[r.status])}</div>
              </div>
              {CHECK_HELP[helpKey] && (
                <div className={`help-tooltip ${isOpen ? "help-panel-open" : ""}`}>{t(CHECK_HELP[helpKey] ?? "")}</div>
              )}
              {r.status !== "geschikt" && r.detail && (
                <div className="check-detail">{r.detail}</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="stats-grid">
        {[
          {
            key: "warmtevraag",
            label: "Warmtevraag",
            value: heat.energy_demand_kwh > 0 ? `${formatNumber(heat.energy_demand_kwh, lang)} kWh` : "-",
          },
          {
            key: "capaciteitsdekking",
            label: "Capaciteitsdekking",
            value: heat.coverage_pct > 0 ? `${heat.coverage_pct}%` : "-",
          },
          {
            key: "aanbevolen_pomp",
            label: "Aanbevolen pomp",
            value: t(PUMP_LABEL[pump]),
          },
          {
            key: "max_binnentemp",
            label: "Max. binnentemp",
            value:
              heat.estimated_max_indoor_temp_c !== null && heat.estimated_max_indoor_temp_c !== undefined
                ? `${heat.estimated_max_indoor_temp_c}°C`
                : "-",
          },
        ].map((stat) => {
          const isOpen = openStat === stat.key;
          return (
            <div className="stat-item" key={stat.key}>
              <div className="stat-label-row">
                <div className="stat-label">{t(stat.label)}</div>
                <HelpIcon open={isOpen} onClick={() => toggleStat(stat.key)} label={`${t("Uitleg")} ${t(stat.label)}`} />
              </div>
              <div className="stat-value">{stat.value}</div>
              <div className={`help-tooltip ${isOpen ? "help-panel-open" : ""}`}>{t(STAT_HELP[stat.key] ?? "")}</div>
            </div>
          );
        })}
      </div>
    </aside>
  );
}
